import { LanguagesEnum } from '@/api/enums/languages.enum';
import { campaignGenerationLanguagesDropdown } from '@/api/enums/phishing-languages.enum';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  CampaignTemplate,
  GeneratedTemplatePayloadType,
  SimulationWithAssistancePlanData,
} from '@/client/campaigns';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  useCreateReconAiCampaign,
  useGenerateReconAiCampaignTemplate,
} from '@/hooks/query/recon-ai.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DashedSpinner, FlexContainer } from '@/ui';
import { AppButton } from '@/ui/buttons';
import {
  isDemoRestrictedMode,
  replaceEmailTemplatePlaceholders,
  stripUnwantedHtmlTags,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { CAMPAIGN_GENERATION_LOADING } from '../../recommendation/constants';

type PlanWithAssistanceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateSchedule?: (data: SimulationWithAssistancePlanData) => void;
  isSingleSimulation?: boolean;
  onUseEmailTemplate?: (campaignTemplate?: CampaignTemplate) => void;
};

const StyledDialog = styled(Dialog)`
  width: 600px;
  max-width: 90vw;
  max-height: 638px;

  .p-dialog-header {
    padding: var(--default-padding) var(--default-padding) 0
      var(--default-padding);
  }

  .p-dialog-content {
    padding: var(--default-padding);
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: var(--small-font-size);
  background-color: ${({ active, completed }) =>
    active || completed ? 'var(--green-color)' : 'var(--white-main)'};
  color: ${({ active, completed }) =>
    active || completed ? 'white' : 'var(--black-main)'};
  border: 1px solid
    ${({ active }) => (active ? 'var(--green-color)' : 'var(--gray-dark)')};
`;

const QuestionSection = styled.div`
  margin-bottom: var(--default-padding);
`;

const MainQuestion = styled.h3`
  font-size: var(--semi-big-font-size);
  font-weight: 600;
  color: var(--black-main);
  margin: 0 0 var(--xsmall-padding) 0;
  text-align: center;
`;

const SubQuestion = styled.p`
  font-size: var(--small-font-size);
  color: var(--black-main);
  margin: 0 0 var(--medium-padding) 0;
  text-align: center;
`;

const InputRow = styled(FlexContainer)`
  margin-bottom: var(--default-padding);
  width: 100%;
`;

const RecommendationBox = styled(FlexContainer)`
  padding: var(--medium-padding);
  border-radius: var(--box-border-radius);
  margin-bottom: var(--medium-padding);
  gap: var(--small-padding);
  background-color: var(--blue-opacity);
`;

const RecommendationIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: var(--medium-font-size);
  font-weight: 600;
  color: var(--blue-dark);
  flex-shrink: 0;
`;

const RecommendationContent = styled.div`
  flex: 1;
`;

const RecommendationTitle = styled.div`
  font-size: var(--small-font-size);
  color: var(--black-main);
  line-height: 1.4;
  margin-bottom: var(--xsmall-padding);
`;

const RecommendationList = styled.ul`
  margin: 0;
  padding-left: var(--medium-padding);
  font-size: var(--small-font-size);
  color: var(--black-main);
  line-height: 1.4;
`;

const RecommendationItem = styled.li`
  margin-bottom: var(--xxsmall-padding);
`;

const ActionButtons = styled.div<{ isLeft?: boolean }>`
  display: flex;
  justify-content: ${({ isLeft }) => (isLeft ? 'flex-start' : 'flex-end')};
  gap: var(--small-padding);
  align-items: center;
  margin-top: var(--default-padding);
  width: 100%;
`;

const StyledTemplateContainer = styled.div`
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);

  h4 {
    font-size: var(--medium-font-size);
    font-weight: 700;
    color: var(--black-main);
    margin-bottom: var(--default-padding);
  }
`;

const StyledTag = styled(FlexContainer)`
  width: fit-content;
  align-items: center;
  justify-content: flex-start;
  min-height: 19px;
  background-color: color-mix(in srgb, var(--green-color) 10%, transparent);
  color: var(--green-color);
  border-radius: var(--grand-border-radius);
  padding-inline: var(--small-padding);
  font-size: var(--xxsmall-font-size);
  line-height: var(--xxsmall-line-height);
  margin-bottom: var(--medium-padding);
`;

const LoadingMessage = styled.span`
  display: block;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--black-main);
`;

export const PlanWithAssistanceModal: React.FC<
  PlanWithAssistanceModalProps
> = ({
  isOpen,
  onClose,
  onCreateSchedule,
  isSingleSimulation = false,
  onUseEmailTemplate,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [timePeriod, setTimePeriod] = useState(12);
  const [numberOfSimulations, setNumberOfSimulations] = useState(8);
  const [selectedLanguage, setSelectedLanguage] = useState(LanguagesEnum.EN);
  const [isLoading, setIsLoading] = useState(false);
  const [generationFailed, setGenerationFailed] = useState(false);
  const [generatedTemplate, setGeneratedTemplate] = useState<
    GeneratedTemplatePayloadType | undefined
  >();
  const [messageIndex, setMessageIndex] = useState<number>(0);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activeGenerationTokenRef = useRef<string | null>(null);

  const timePeriodOptions = [
    { label: t('phishing.simulation.timePeriod.3months'), value: 3 },
    { label: t('phishing.simulation.timePeriod.6months'), value: 6 },
    { label: t('phishing.simulation.timePeriod.12months'), value: 12 },
  ];

  const messages = useMemo(() => CAMPAIGN_GENERATION_LOADING(t), [t]);
  const currentMessage = messages[messageIndex] || messages[0];

  useEffect(() => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }

    if (isLoading && messages.length > 0) {
      setMessageIndex(0);

      messageIntervalRef.current = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
      }, 1500);
    }

    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    };
  }, [isLoading, messages]);

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleCreateSchedule = () => {
    if (!onCreateSchedule) return;
    onCreateSchedule({
      timePeriod,
      numberOfSimulations,
    });
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(1);
    setTimePeriod(12);
    setNumberOfSimulations(8);
    onClose();
    setIsLoading(false);
    setGenerationFailed(false);
    setGeneratedTemplate(undefined);
    setMessageIndex(0);
    setNotificationParam(undefined);
    setSelectedLanguage(LanguagesEnum.EN);
    activeGenerationTokenRef.current = null;
  };

  const { lastMessage, setNotificationParam, notificationParam } =
    useNotifications(client.reconAi.reconAiCampaignNotifyUrl);

  useEffect(() => {
    if (!notificationParam) return;
    handleNotifyReconAiCampaignGenerated(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleNotifyReconAiCampaignGenerated = (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'recon-ai.template-generation.success',
        'recon-ai.template-generation.error',
      ].includes(messages?.data?.event)
    )
      return;

    const eventToken = messages?.data?.payload?.generationToken;
    if (
      activeGenerationTokenRef.current &&
      eventToken !== activeGenerationTokenRef.current
    )
      return;

    if (messages?.data?.event === 'recon-ai.template-generation.success') {
      setGeneratedTemplate(messages?.data?.payload.response);
      handleNext();
    }

    if (messages?.data?.event === 'recon-ai.template-generation.error') {
      setGenerationFailed(true);
    }

    setIsLoading(false);
    setNotificationParam(undefined);
  };

  const createAiCampaign = useCreateReconAiCampaign();
  const handleCreateCampaign = async () => {
    const genToken = v4();
    activeGenerationTokenRef.current = genToken;
    setIsLoading(true);
    setNotificationParam(currentAccount?.id as string);
    try {
      await createAiCampaign.create({
        accountId: currentAccount?.id as string,
        language: selectedLanguage,
        generationToken: genToken,
      });
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setNotificationParam(undefined);
      setIsLoading(false);
    }
  };

  const generateAiTemplate = useGenerateReconAiCampaignTemplate();
  const handleGenerateTemplate = async () => {
    if (!generatedTemplate?.template) return;
    setIsLoading(true);
    try {
      const response = await generateAiTemplate.generate({
        accountId: currentAccount?.id as string,
        emailTemplate: generatedTemplate?.template,
        subject: generatedTemplate?.subject,
        phishingSign: generatedTemplate?.phishingSign,
        senderName: generatedTemplate?.senderName,
        language: selectedLanguage,
      });
      onUseEmailTemplate?.(response as CampaignTemplate);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setNotificationParam(undefined);
    } finally {
      handleClose();
    }
  };

  return (
    <StyledDialog
      visible={isOpen}
      onHide={handleClose}
      modal
      draggable={false}
      resizable={false}
      className="v2"
    >
      {isLoading && (
        <FlexContainer direction="column">
          <h2 className="mt-0 mb-5">
            {t('phishing.simulations.modal.aiSimulation.loadingTitle')}
          </h2>
          <DashedSpinner size={86} strokeWidth={17} />
          <LoadingMessage role="status" aria-live="polite">
            {currentMessage}
          </LoadingMessage>
        </FlexContainer>
      )}
      {!isLoading && generationFailed && (
        <FlexContainer direction="column">
          <h2 className="mt-0 mb-5">
            {t('phishing.simulations.modal.aiSimulation.generationFailedTitle')}
          </h2>
          <p>
            {t(
              'phishing.simulations.modal.aiSimulation.generationFailedDescription',
            )}
          </p>
        </FlexContainer>
      )}
      {!isLoading && !generationFailed && (
        <>
          <h2 className="mt-0">
            {isSingleSimulation
              ? t('phishing.simulations.modal.generateSimulation')
              : t('phishing.simulation.planner.title')}
          </h2>
          <FlexContainer
            direction="row"
            gap={12}
            style={{ marginBottom: 'var(--default-padding)' }}
          >
            <StepCircle active={currentStep === 1} completed={currentStep > 1}>
              1
            </StepCircle>
            <StepCircle active={currentStep === 2} completed={false}>
              2
            </StepCircle>
          </FlexContainer>

          {currentStep === 1 && (
            <>
              <QuestionSection>
                <MainQuestion>
                  {isSingleSimulation
                    ? t(
                        'phishing.simulations.modal.aiSimulation.chooseLanguage',
                      )
                    : t('phishing.simulation.step1.title')}
                </MainQuestion>
                <SubQuestion>
                  {isSingleSimulation
                    ? t(
                        'phishing.simulations.modal.aiSimulation.chooseLanguage.descr',
                      )
                    : t('phishing.simulation.step1.subtitle')}
                </SubQuestion>
              </QuestionSection>

              <InputRow gap={24}>
                {isSingleSimulation ? (
                  <>
                    <Dropdown
                      filter
                      value={selectedLanguage}
                      options={campaignGenerationLanguagesDropdown}
                      onChange={(e) => setSelectedLanguage(e.value)}
                      placeholder={t('generic.select')}
                      style={{ minWidth: '250px' }}
                    />
                  </>
                ) : (
                  <Dropdown
                    value={timePeriod}
                    options={timePeriodOptions}
                    onChange={(e) => setTimePeriod(e.value)}
                    placeholder={t('phishing.simulation.step1.placeholder')}
                    style={{ width: '250px' }}
                  />
                )}
                <AppButton
                  label={t('button.next')}
                  onClick={
                    isSingleSimulation
                      ? isDemoAccount
                        ? undefined
                        : handleCreateCampaign
                      : handleNext
                  }
                  isDisabled={
                    isSingleSimulation ? !selectedLanguage : !timePeriod
                  }
                />
              </InputRow>
            </>
          )}

          {currentStep === 2 &&
            (isSingleSimulation ? (
              <>
                <StyledTemplateContainer>
                  <StyledTag>
                    <b>
                      {t(
                        'phishing.simulations.modal.aiSimulation.template.tag',
                      ) + ':'}
                    </b>
                    <span className="ml-1">
                      {t(
                        `campaign.template.${generatedTemplate?.phishingSign}`,
                      )}
                    </span>
                  </StyledTag>
                  <h4 className="mt-0 mb-2">{generatedTemplate?.subject}</h4>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: replaceEmailTemplatePlaceholders(
                        stripUnwantedHtmlTags(
                          generatedTemplate?.template ?? '',
                        ),
                      ),
                    }}
                  />
                </StyledTemplateContainer>
                <ActionButtons isLeft={true}>
                  <AppButton
                    label={t('phishing.simulations.modal.aiSimulation.useMail')}
                    onClick={handleGenerateTemplate}
                  />
                  <AppButton
                    severity="secondary"
                    type="outlined"
                    label={t(
                      'phishing.simulations.modal.aiSimulation.generateAgain',
                    )}
                    onClick={handleCreateCampaign}
                  />
                </ActionButtons>
              </>
            ) : (
              <>
                <QuestionSection>
                  <MainQuestion>
                    {t('phishing.simulation.step2.title')}
                  </MainQuestion>
                  <SubQuestion>
                    {t('phishing.simulation.step2.subtitle')}
                  </SubQuestion>
                </QuestionSection>
                <InputRow direction="column" gap={4}>
                  <InputNumber
                    value={numberOfSimulations}
                    onChange={(e) => setNumberOfSimulations(e.value ?? 0)}
                    min={1}
                    className="w-full"
                  />
                  {numberOfSimulations > 24 && (
                    <small className="p-error w-full">
                      {t('phishing.simulation.numberOfSimulations.error')}
                    </small>
                  )}
                </InputRow>

                <RecommendationBox className="w-full">
                  <RecommendationIcon>
                    <i className="pi pi-info-circle" />
                  </RecommendationIcon>
                  <RecommendationContent>
                    <RecommendationTitle>
                      {t('phishing.simulation.recommendation.title')}
                    </RecommendationTitle>
                    <RecommendationList>
                      <RecommendationItem>
                        {t('phishing.simulation.recommendation.item1')}
                      </RecommendationItem>
                      <RecommendationItem>
                        {t('phishing.simulation.recommendation.item2')}
                      </RecommendationItem>
                    </RecommendationList>
                  </RecommendationContent>
                </RecommendationBox>
                <ActionButtons>
                  <AppButton
                    severity="secondary"
                    type="outlined"
                    label={t('button.back')}
                    onClick={handleBack}
                  />
                  <AppButton
                    label={t('phishing.simulation.step2.createButton')}
                    onClick={isDemoAccount ? undefined : handleCreateSchedule}
                    isDisabled={
                      !numberOfSimulations || numberOfSimulations > 24
                    }
                  />
                </ActionButtons>
              </>
            ))}
        </>
      )}
    </StyledDialog>
  );
};
